export class AudioPitchAnalyzer {
    constructor() {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.bufferLength = this.analyser.frequencyBinCount;
        this.dataArray = new Float32Array(this.bufferLength);
        this.sampleRate = this.audioContext.sampleRate;

        // Adjusted downsampling rate for better low frequency detection
        this.downsampleRate = 8000; // Reduced from 16000

        // Add smoothing for pitch stability
        this.pitchHistory = [];
        this.historySize = 5;
    }

    connectStream(stream) {
        const source = this.audioContext.createMediaStreamSource(stream);
        source.connect(this.analyser);
    }

    downsample(buffer) {
        const downsampleFactor = Math.floor(this.sampleRate / this.downsampleRate);
        const downsampledLength = Math.floor(buffer.length / downsampleFactor);
        const downsampledBuffer = new Float32Array(downsampledLength);

        for (let i = 0; i < downsampledLength; i++) {
            downsampledBuffer[i] = buffer[i * downsampleFactor];
        }

        return downsampledBuffer;
    }

    getPitch(inputBuffer, inputSampleRate) {
        // Get audio data
        if (!inputBuffer) {
            this.analyser.getFloatTimeDomainData(this.dataArray);
        } else {
            this.dataArray = inputBuffer;
            this.sampleRate = inputSampleRate;
        }

        // Downsample the data
        const downsampledData = this.downsample(this.dataArray);

        // Increased RMS threshold for better signal detection
        const rms = Math.sqrt(downsampledData.reduce((sum, val) => sum + val * val, 0) / downsampledData.length);
        if (rms < 0.02) { // Increased from 0.01
            return null;
        }

        // Normalize the signal
        const normalizedData = new Float32Array(downsampledData.length);
        for (let i = 0; i < downsampledData.length; i++) {
            normalizedData[i] = downsampledData[i] / rms;
        }

        // Adjusted frequency range for male voices
        const minPeriod = Math.floor(this.downsampleRate / 300);
        const maxPeriod = Math.floor(this.downsampleRate / 30);

        let maxCorrelation = 0;
        let bestPeriod = 0;

        for (let lag = minPeriod; lag <= maxPeriod; lag++) {
            let correlation = 0;
            let n = 0;

            for (let i = 0; i < normalizedData.length - lag; i++) {
                correlation += normalizedData[i] * normalizedData[i + lag];
                n++;
            }

            correlation = correlation / n;

            if (correlation > maxCorrelation) {
                maxCorrelation = correlation;
                bestPeriod = lag;
            }
        }

        // Lowered correlation threshold
        if (maxCorrelation > 0.3) { // Changed from 0.5
            const fundamentalFreq = this.downsampleRate / bestPeriod;

            // Apply smoothing
            this.pitchHistory.push(fundamentalFreq);
            if (this.pitchHistory.length > this.historySize) {
                this.pitchHistory.shift();
            }

            // Return median pitch for stability
            const sortedPitches = [...this.pitchHistory].sort((a, b) => a - b);
            return sortedPitches[Math.floor(sortedPitches.length / 2)];
        }

        return null;
    }

    destroy() {
        if (this.audioContext.state !== 'closed') {
            this.audioContext.close();
        }
    }
}